import { Entity, LiveData } from '@toeverything/infra';
import Fuse from 'fuse.js';

import type { DocsService } from '../../doc';
import type { QuickSearchSession } from '../providers/quick-search-provider';
import type { QuickSearchGroup } from '../types/group';
import type { QuickSearchItem } from '../types/item';
import { highlighter } from '../utils/highlighter';

const group: QuickSearchGroup = {
  id: 'notes',
  label: { i18nKey: 'Notes' },
  score: 8,
};

interface NotesEntryLike {
  entryId: string;
  sourcePassage: string;
  sourceDocId?: string;
  tags?: string[];
  theme?: string;
  userNote?: string;
}

interface NotesPayload {
  notesDocId: string;
  entryId: string;
}

/**
 * Cmdk quick-search session for Notes entries — keyboard-first
 * researchers can pop the modal (Ctrl/Cmd-K) and jump straight to
 * any clipping by passage substring / tag / theme / personal note.
 *
 * Data flows from `DocsService.allDocProperties$` — the same source
 * the `/notes` page reads — so a fresh clipping shows up in cmdk on
 * the next keystroke without any extra plumbing.
 */
export class NotesQuickSearchSession
  extends Entity
  implements QuickSearchSession<'notes', NotesPayload>
{
  constructor(private readonly docsService: DocsService) {
    super();
  }

  query$ = new LiveData('');

  /**
   * Resolve the workspace's notes accumulator + the flat index in one
   * pass. Same predicate as `useNotesIndex` — first doc carrying a
   * parseable `custom:notesIndex` wins.
   */
  private readonly notesData$ = this.docsService.allDocProperties$.map(
    allProps => {
      let notesDocId: string | null = null;
      let entries: NotesEntryLike[] = [];
      if (!allProps) return { notesDocId, entries };
      for (const [docId, props] of Object.entries(allProps)) {
        const raw = (props as Record<string, unknown>)['custom:notesIndex'];
        if (typeof raw !== 'string') continue;
        try {
          const parsed = JSON.parse(raw);
          if (!Array.isArray(parsed)) continue;
          notesDocId = docId;
          entries = parsed as NotesEntryLike[];
          break;
        } catch {
          continue;
        }
      }
      return { notesDocId, entries };
    }
  );

  items$: LiveData<QuickSearchItem<'notes', NotesPayload>[]> =
    LiveData.computed(get => {
      const query = get(this.query$).trim();
      if (!query) return [];
      const { notesDocId, entries } = get(this.notesData$);
      if (!notesDocId || entries.length === 0) return [];

      // Fuse weights — passage carries the user's primary intent;
      // userNote / tags / theme are secondary. Threshold tuned to
      // tolerate partial matches without producing noise.
      const fuse = new Fuse(entries, {
        keys: [
          { name: 'sourcePassage', weight: 0.6 },
          { name: 'userNote', weight: 0.2 },
          { name: 'tags', weight: 0.1 },
          { name: 'theme', weight: 0.1 },
        ],
        includeMatches: true,
        includeScore: true,
        ignoreLocation: true,
        threshold: 0.35,
      });

      const result = fuse.search(query, { limit: 12 });

      return result.map<QuickSearchItem<'notes', NotesPayload>>(
        ({ item, matches, score = 1 }) => {
          const passageMatches = matches
            ?.filter(m => m.key === 'sourcePassage')
            .flatMap(m =>
              m.indices.map(([s, e]) => [s, e + 1] as [number, number])
            );
          const title =
            (highlighter(
              item.sourcePassage,
              '<b>',
              '</b>',
              passageMatches ?? []
            ) ??
              item.sourcePassage) || { i18nKey: 'Untitled' };
          const subParts: string[] = [];
          if (item.theme) subParts.push(`theme: ${item.theme}`);
          if (item.tags?.length) subParts.push(item.tags.join(', '));
          if (item.userNote) subParts.push(`note: ${item.userNote}`);
          return {
            id: 'notes:' + item.entryId,
            source: 'notes',
            label: {
              title,
              subTitle: subParts.length > 0 ? subParts.join(' · ') : undefined,
            },
            group,
            score: 1 - score,
            payload: { notesDocId, entryId: item.entryId },
          };
        }
      );
    });

  query(query: string) {
    this.query$.next(query);
  }
}
